import { Component } from '@theme/component';
import { trapFocus, removeTrapFocus } from '@theme/focus';

/**
 * @typedef {object} MenuNode
 * @property {string} label
 * @property {string} href
 * @property {MenuNode[]} [children]
 * @property {string} [badge]
 * @property {boolean} [accent]
 * @property {string | null} [thumbnail]
 * @property {string} [id]
 */

/**
 * @typedef {object} Refs
 * @property {HTMLDetailsElement} details
 * @property {HTMLElement} summary
 * @property {HTMLElement} drawer
 * @property {HTMLElement} scrim
 * @property {HTMLElement} panes
 */

/**
 * PHL mobile drawer — push panes + leaf accordion (design prototype).
 * @extends {Component<Refs>}
 */
class PhlHeaderDrawer extends Component {
  requiredRefs = ['details', 'summary', 'drawer', 'scrim', 'panes'];

  /** @type {HTMLElement[]} */
  #stack = [];

  /** @type {MenuNode[]} */
  #menu = [];

  /** @type {((event: KeyboardEvent) => void) | null} */
  #onKeyUp = null;

  connectedCallback() {
    super.connectedCallback();
    this.#enhance();
    this.#onKeyUp = (event) => {
      if (event.key === 'Escape' && this.isOpen) this.close();
    };
    this.addEventListener('keyup', this.#onKeyUp);
  }

  /**
   * Section hydration can remorph the drawer after first connect — re-enhance.
   */
  updatedCallback() {
    super.updatedCallback?.();
    this.#enhance();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    if (this.#onKeyUp) this.removeEventListener('keyup', this.#onKeyUp);
    removeTrapFocus();
  }

  get isOpen() {
    return this.refs.details.hasAttribute('open');
  }

  /**
   * @param {Event} [event]
   */
  toggle(event) {
    event?.preventDefault?.();

    if (this.isOpen) {
      this.close();
    } else {
      this.open();
    }
  }

  open() {
    this.#enhance();

    const { details, summary, drawer, scrim } = this.refs;
    summary.setAttribute('aria-expanded', 'true');
    details.setAttribute('open', '');
    drawer.classList.add('is-open');
    scrim.classList.add('is-visible');

    requestAnimationFrame(() => {
      details.classList.add('is-open');
      trapFocus(drawer);
    });
  }

  /**
   * @param {Event} [event]
   */
  close(event) {
    event?.preventDefault?.();

    const { details, summary, drawer, scrim } = this.refs;
    summary.setAttribute('aria-expanded', 'false');
    details.classList.remove('is-open');
    drawer.classList.remove('is-open');
    scrim.classList.remove('is-visible');

    window.setTimeout(() => {
      details.removeAttribute('open');
      removeTrapFocus();
      summary.focus();
      this.#resetPanes();
    }, 320);
  }

  /**
   * Progressive enhancement: keep server HTML for no-JS; replace with push panes when JSON is available.
   */
  #enhance() {
    this.#menu = this.#readMenuData();
    if (!this.#menu.length) return;

    this.classList.add('is-enhanced');
    this.#resetPanes();
  }

  #resetPanes() {
    const panes = this.refs.panes;
    if (!(panes instanceof HTMLElement)) return;

    panes.replaceChildren();
    this.#stack = [];
    const root = this.#makePane({ label: 'Menu', href: '#', children: this.#menu }, true);
    panes.appendChild(root);
    this.#stack = [root];
  }

  #readMenuData() {
    /** @type {MenuNode[]} */
    const items = [];
    const sources = [
      ...this.querySelectorAll('[data-phl-menu-source] [data-phl-menu-item]'),
      ...this.querySelectorAll('[data-phl-menu-item]'),
      ...document.querySelectorAll('phl-header-menu [data-phl-menu-item]'),
    ];

    const seen = new Set();

    for (const el of sources) {
      if (!(el instanceof HTMLScriptElement)) continue;
      try {
        const data = JSON.parse(el.textContent || 'null');
        if (!data?.label || seen.has(data.id || data.label)) continue;
        seen.add(data.id || data.label);
        items.push(data);
      } catch {
        // skip invalid JSON
      }
    }

    return items;
  }

  /**
   * @param {MenuNode} node
   * @param {boolean} isRoot
   */
  #makePane(node, isRoot) {
    const pane = document.createElement('div');
    pane.className = `phl-mm-pane${isRoot ? ' phl-mm-pane--root' : ''} is-current`;

    if (!isRoot) {
      const crumb = document.createElement('button');
      crumb.type = 'button';
      crumb.className = 'phl-mm-crumb';
      crumb.innerHTML = `${this.#iconBack()}<span>${this.#escapeHtml(this.dataset.backLabel || 'Back')}</span>`;
      crumb.addEventListener('click', () => this.#popPane());
      pane.appendChild(crumb);
    }

    const scroll = document.createElement('div');
    scroll.className = 'phl-mm-scroll';

    if (!isRoot) {
      const title = document.createElement('div');
      title.className = 'phl-mm-title';
      title.textContent = node.label;
      scroll.appendChild(title);

      const all = document.createElement('a');
      all.className = 'phl-mm-all';
      all.href = node.href || '#';
      const seeAll = (this.dataset.seeAll || 'See all in {{ title }}').replace('{{ title }}', node.label);
      const count = this.#countLeaves(node);
      const itemsLabel = (this.dataset.itemsLabel || '{{ count }} items')
        .replace('{{ count }}', String(count));
      all.innerHTML = `<span>${this.#escapeHtml(seeAll)}</span><span>${this.#escapeHtml(itemsLabel)} →</span>`;
      scroll.appendChild(all);
    }

    const ul = document.createElement('ul');
    ul.className = 'phl-mm-list';

    for (const child of node.children || []) {
      ul.appendChild(this.#makeListItem(child, isRoot));
    }

    scroll.appendChild(ul);

    if (isRoot) {
      scroll.appendChild(this.#makeUtil());
      scroll.appendChild(this.#makeSocial());
      scroll.appendChild(this.#makeFoot());
    }

    pane.appendChild(scroll);
    return pane;
  }

  /**
   * @param {MenuNode} child
   * @param {boolean} isRoot
   */
  #makeListItem(child, isRoot) {
    const li = document.createElement('li');
    const kids = child.children || [];
    const pill =
      child.badge === 'new'
        ? `<span class="phl-pill phl-pill--dark">${this.#escapeHtml('New')}</span>`
        : child.badge === 'sale'
          ? `<span class="phl-pill">${this.#escapeHtml('Sale')}</span>`
          : '';
    const thumb = isRoot ? this.#thumbHtml(child.thumbnail) : '';

    if (kids.length && this.#hasGrandchildren(child)) {
      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'has-children';
      button.innerHTML = `${thumb}<span class="phl-mm-lbl">${this.#escapeHtml(child.label)}</span>${pill}${this.#iconChev()}`;
      button.addEventListener('click', () => this.#pushPane(child));
      li.appendChild(button);
    } else if (kids.length) {
      li.className = 'phl-mm-acc';
      const button = document.createElement('button');
      button.type = 'button';
      button.setAttribute('aria-expanded', 'false');
      button.innerHTML = `${thumb}<span class="phl-mm-lbl">${this.#escapeHtml(child.label)}</span><span class="phl-mm-cnt">${kids.length}</span>${this.#iconChev()}`;
      const body = document.createElement('div');
      body.className = 'phl-mm-acc-body';
      body.hidden = true;

      for (const leaf of kids) {
        const a = document.createElement('a');
        a.href = leaf.href || '#';
        a.textContent = leaf.label;
        body.appendChild(a);
      }

      const seeAll = document.createElement('a');
      seeAll.href = child.href || '#';
      seeAll.className = 'phl-mm-acc-all';
      seeAll.textContent = `${this.dataset.seeAllArrow || 'See all'} →`;
      body.appendChild(seeAll);

      button.addEventListener('click', () => {
        const open = li.classList.toggle('is-open');
        button.setAttribute('aria-expanded', open ? 'true' : 'false');
        body.hidden = !open;
      });

      li.appendChild(button);
      li.appendChild(body);
    } else {
      const a = document.createElement('a');
      a.href = child.href || '#';
      a.innerHTML = `${thumb}<span class="phl-mm-lbl">${this.#escapeHtml(child.label)}</span>${pill}`;
      li.appendChild(a);
    }

    return li;
  }

  /**
   * @param {MenuNode} node
   */
  #pushPane(node) {
    const current = this.#stack[this.#stack.length - 1];
    if (current) {
      current.classList.remove('is-current');
      current.classList.add('is-past');
    }

    const next = this.#makePane(node, false);
    next.classList.remove('is-current');
    this.refs.panes.appendChild(next);
    next.getBoundingClientRect();
    requestAnimationFrame(() => next.classList.add('is-current'));
    this.#stack.push(next);
  }

  #popPane() {
    if (this.#stack.length < 2) return;

    const current = this.#stack.pop();
    if (!current) return;
    current.classList.remove('is-current');

    const prev = this.#stack[this.#stack.length - 1];
    if (prev) {
      prev.classList.remove('is-past');
      prev.classList.add('is-current');
    }

    window.setTimeout(() => current.remove(), 300);
  }

  #makeUtil() {
    const util = document.createElement('div');
    util.className = 'phl-mm-util';

    const rows = [
      { href: this.dataset.accountUrl || '#', label: this.dataset.accountLabel || 'Account', icon: this.#iconUser(), pro: false },
      // Wishlist temporarily hidden
      // { href: this.dataset.wishlistUrl || '#', label: this.dataset.wishlistLabel || 'Wishlist', icon: this.#iconHeart(), pro: false },
      { href: this.dataset.supportUrl || '#', label: this.dataset.supportLabel || 'Support', icon: this.#iconHead(), pro: false },
      { href: this.dataset.proUrl || '#', label: this.dataset.proLabel || 'Pro', icon: this.#iconPin(), pro: true },
    ];

    for (const row of rows) {
      if (!row.href || row.href === '#') {
        if (row.pro || row.label) {
          // still show configured labels even without URL
        }
      }
      const a = document.createElement('a');
      a.href = row.href || '#';
      if (row.pro) a.className = 'is-pro';
      a.innerHTML = `${row.icon}<span>${this.#escapeHtml(row.label)}</span>`;
      util.appendChild(a);
    }

    return util;
  }

  #makeSocial() {
    const wrap = document.createElement('div');
    wrap.className = 'phl-mm-social';

    const links = [
      { href: this.dataset.socialInstagram, label: 'Instagram', icon: this.#iconIg() },
      { href: this.dataset.socialFacebook, label: 'Facebook', icon: this.#iconFb() },
      { href: this.dataset.socialTiktok, label: 'TikTok', icon: this.#iconTt() },
    ];

    for (const link of links) {
      if (!link.href) continue;
      const a = document.createElement('a');
      a.href = link.href;
      a.setAttribute('aria-label', link.label);
      a.target = '_blank';
      a.rel = 'noopener noreferrer';
      a.innerHTML = link.icon;
      wrap.appendChild(a);
    }

    if (!wrap.childElementCount) wrap.hidden = true;
    return wrap;
  }

  #makeFoot() {
    const foot = document.createElement('div');
    foot.className = 'phl-mm-foot';
    const phone = this.dataset.footerPhone || '';
    const hours = this.dataset.footerHours || '';
    const links = this.dataset.footerLinks || '';

    if (phone || hours) {
      const line = document.createElement('span');
      line.innerHTML = phone
        ? `<strong>${this.#escapeHtml(phone)}</strong>${hours ? ` · ${this.#escapeHtml(hours)}` : ''}`
        : this.#escapeHtml(hours);
      foot.appendChild(line);
    }

    if (links) {
      const line = document.createElement('span');
      line.textContent = links;
      foot.appendChild(line);
    }

    if (!foot.childElementCount) foot.hidden = true;
    return foot;
  }

  /**
   * @param {MenuNode} node
   */
  #hasGrandchildren(node) {
    return (node.children || []).some((child) => (child.children || []).length > 0);
  }

  /**
   * @param {MenuNode} node
   */
  #countLeaves(node) {
    const kids = node.children || [];
    if (!kids.length) return 1;
    return kids.reduce((sum, child) => sum + this.#countLeaves(child), 0);
  }

  /**
   * @param {string | null | undefined} src
   */
  #thumbHtml(src) {
    if (src) {
      return `<img class="phl-mm-thumb" src="${this.#escapeHtml(src)}" alt="" width="38" height="38" loading="lazy">`;
    }
    return '<span class="phl-mm-thumb" aria-hidden="true"></span>';
  }

  #iconChev() {
    return '<span class="phl-mm-chev" aria-hidden="true"><svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"><path d="m9 6 6 6-6 6"/></svg></span>';
  }

  #iconBack() {
    return '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="m15 6-6 6 6 6"/></svg>';
  }

  #iconUser() {
    return '<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><circle cx="12" cy="8" r="4"/><path d="M4 21c0-4 3.6-6 8-6s8 2 8 6"/></svg>';
  }

  #iconHeart() {
    return '<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M12 20s-7-4.6-9.3-9C1.2 8.3 2.5 5 5.8 5 8 5 9.3 6.6 12 9c2.7-2.4 4-4 6.2-4 3.3 0 4.6 3.3 3.1 6-2.3 4.4-9.3 9-9.3 9z"/></svg>';
  }

  #iconHead() {
    return '<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" aria-hidden="true"><path d="M4 12a8 8 0 0 1 16 0v5a3 3 0 0 1-3 3h-2"/><rect x="2" y="12" width="4" height="6" rx="1.5"/><rect x="18" y="12" width="4" height="6" rx="1.5"/></svg>';
  }

  #iconPin() {
    return '<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M12 21s7-5.5 7-11a7 7 0 1 0-14 0c0 5.5 7 11 7 11z"/><circle cx="12" cy="10" r="2.5"/></svg>';
  }

  #iconIg() {
    return '<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" aria-hidden="true"><rect x="3" y="3" width="18" height="18" rx="5"/><circle cx="12" cy="12" r="4"/><circle cx="17.5" cy="6.5" r="1" fill="currentColor" stroke="none"/></svg>';
  }

  #iconFb() {
    return '<svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M14 9h3V6h-3c-2.2 0-3.5 1.5-3.5 3.7V11H8v3h2.5v7h3v-7H16l.5-3h-3V9.8c0-.5.3-.8 1-.8z"/></svg>';
  }

  #iconTt() {
    return '<svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M16 3c.3 2 1.6 3.5 3.5 3.8V9.5C18 9.4 16.8 9 16 8.4V15a5.5 5.5 0 1 1-5.5-5.5c.3 0 .6 0 .9.1v2.7a2.8 2.8 0 1 0 2 2.7V3H16z"/></svg>';
  }

  /**
   * @param {string} value
   */
  #escapeHtml(value) {
    return value
      .replaceAll('&', '&amp;')
      .replaceAll('<', '&lt;')
      .replaceAll('>', '&gt;')
      .replaceAll('"', '&quot;');
  }
}

if (!customElements.get('phl-header-drawer')) {
  customElements.define('phl-header-drawer', PhlHeaderDrawer);
}
